/**
 * Página de Paquetes — vista moderna con tabla, búsqueda, filtros, slide-over.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Switch, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Avatar, Divider, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EventIcon from '@mui/icons-material/Event';
import CollectionsIcon from '@mui/icons-material/Collections';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GaleriaImagenes from '../common/GaleriaImagenes';
import LuggageIcon from '@mui/icons-material/Luggage';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}
function esSuperadmin() { return getUserRole() === 'superadmin'; }
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  // X-User-Id se usa en endpoints que auditan quién hizo el cambio (mig auditoría).
  const userId = localStorage.getItem('user_id');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
  };
}
function fmtMoney(n) {
  if (n == null) return '—';
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n));
}

const emptyForm = {
  nombre: '', destino_id: '', descripcion: '',
  // Migración 097 — modelo neto + PVP directa
  precio_neto_adulto: '', precio_neto_nino: '',
  precio_pvp_adulto_directa: '', precio_pvp_nino_directa: '',
  precio_bebe: '0',
  hora_salida_default: '08:00', hora_regreso_default: '15:00',
  capacidad_maxima: '50', incluye: '', no_incluye: '', drive_url: '',
};

export default function PaquetesPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();

  const [paquetes, setPaquetes] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDestino, setFilterDestino] = useState('');
  const [filterActivo, setFilterActivo] = useState('');

  // Slide-over edición
  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Confirmación delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(null);  // {ids, run}

  // Migración 099 — Tarifas estacionales del paquete que se está editando
  const [tarifas, setTarifas] = useState([]);
  const [tarifaDialogOpen, setTarifaDialogOpen] = useState(false);
  const [editTarifa, setEditTarifa] = useState(null);
  const emptyTarifaForm = {
    nombre: '', fecha_inicio: '', fecha_fin: '',
    precio_neto_adulto: '', precio_neto_nino: '',
    activa: true, notas: '',
  };
  const [formTarifa, setFormTarifa] = useState(emptyTarifaForm);
  const [savingTarifa, setSavingTarifa] = useState(false);

  // Mejora #3 — Simulador inline de precio para validar setup del paquete
  const [agencias, setAgencias] = useState([]);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const emptySimForm = { fecha: todayISO(), adultos: 2, ninos: 0, bebes: 0, agencia_id: '' };
  const [simForm, setSimForm] = useState(emptySimForm);
  const [simResult, setSimResult] = useState(null);
  const [simulando, setSimulando] = useState(false);

  // Mejora #4 — Histórico de cambios de precio del paquete
  const [historialPrecios, setHistorialPrecios] = useState([]);

  // Mejora #5 — Métricas por paquete (reservas/ingresos/ocupación últimos 30 días)
  const [metricas, setMetricas] = useState({});  // {paquete_id: {reservas, ingresos, ocupacion_pct, ...}}

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes, aRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/destinos?empresa_id=${empresaId}`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/paquetes-metricas?empresa_id=${empresaId}&dias=30`, { headers: getAuthHeaders() }),
      ]);
      setPaquetes(await pRes.json());
      setDestinos(await dRes.json());
      setAgencias(await aRes.json().catch(() => []));
      // Mergeamos métricas en un dict por paquete_id para acceso O(1) en el render
      try {
        const mData = await mRes.json();
        const dict = {};
        (mData.metricas || []).forEach(m => { dict[m.paquete_id] = m; });
        setMetricas(dict);
      } catch (e) { setMetricas({}); }
    } catch (e) { console.error(e); toast.error('Error cargando paquetes'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  // Mejora #3 — Auto-recalcular simulación con debounce de 300ms cuando
  // cambian los inputs. Solo si hay paquete guardado y dialog abierto.
  useEffect(() => {
    if (!editItem || !slideOpen) { setSimResult(null); return; }
    const t = setTimeout(async () => {
      setSimulando(true);
      try {
        const r = await fetch(`${API_BASE}/turismo/paquetes/${editItem.id}/cotizar-preview`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            fecha: simForm.fecha,
            adultos: parseInt(simForm.adultos) || 0,
            ninos: parseInt(simForm.ninos) || 0,
            bebes: parseInt(simForm.bebes) || 0,
            agencia_id: simForm.agencia_id ? parseInt(simForm.agencia_id) : null,
          }),
        });
        if (r.ok) setSimResult(await r.json());
        else setSimResult(null);
      } catch (e) { setSimResult(null); }
      setSimulando(false);
    }, 300);
    return () => clearTimeout(t);
  }, [editItem, slideOpen, simForm.fecha, simForm.adultos, simForm.ninos, simForm.bebes, simForm.agencia_id]);

  // Aplicar búsqueda + filtros
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return paquetes.filter(p => {
      if (q && !(p.nombre || '').toLowerCase().includes(q) && !(p.descripcion || '').toLowerCase().includes(q)) return false;
      if (filterDestino && p.destino_id !== parseInt(filterDestino)) return false;
      if (filterActivo === 'activos' && !p.activo) return false;
      if (filterActivo === 'inactivos' && p.activo) return false;
      return true;
    });
  }, [paquetes, search, filterDestino, filterActivo]);

  // ── Histórico de cambios de precio (Mejora #4) ───────────────────────
  const cargarHistorialPrecios = useCallback(async (paqueteId) => {
    if (!paqueteId) { setHistorialPrecios([]); return; }
    try {
      const r = await fetch(`${API_BASE}/turismo/paquetes/${paqueteId}/historial-precios`,
        { headers: getAuthHeaders() });
      setHistorialPrecios(r.ok ? await r.json() : []);
    } catch (e) { setHistorialPrecios([]); }
  }, []);

  // ── Tarifas estacionales ──────────────────────────────────────────────
  const cargarTarifas = useCallback(async (paqueteId) => {
    if (!paqueteId) { setTarifas([]); return; }
    try {
      const r = await fetch(
        `${API_BASE}/turismo/tarifas-promocionales?empresa_id=${empresaId}&paquete_id=${paqueteId}`,
        { headers: getAuthHeaders() }
      );
      setTarifas(r.ok ? await r.json() : []);
    } catch (e) { console.error(e); setTarifas([]); }
  }, [empresaId]);

  const categorizarTarifa = (t) => {
    if (!t.activa) return 'inactiva';
    const hoy = new Date().toISOString().slice(0, 10);
    if (t.fecha_fin < hoy) return 'vencida';
    if (t.fecha_inicio > hoy) return 'futura';
    return 'vigente';
  };

  const handleNewTarifa = () => {
    setEditTarifa(null);
    setFormTarifa(emptyTarifaForm);
    setTarifaDialogOpen(true);
  };

  const handleEditTarifa = (t) => {
    setEditTarifa(t);
    setFormTarifa({
      nombre: t.nombre,
      fecha_inicio: t.fecha_inicio,
      fecha_fin: t.fecha_fin,
      precio_neto_adulto: String(t.precio_neto_adulto || ''),
      precio_neto_nino: t.precio_neto_nino != null ? String(t.precio_neto_nino) : '',
      activa: t.activa,
      notas: t.notas || '',
    });
    setTarifaDialogOpen(true);
  };

  const handleSaveTarifa = async () => {
    if (!editItem) return toast.error('Guarda el paquete primero');
    if (!formTarifa.nombre.trim()) return toast.error('Falta el nombre de la temporada');
    if (!formTarifa.fecha_inicio || !formTarifa.fecha_fin) return toast.error('Faltan fechas');
    if (formTarifa.fecha_fin < formTarifa.fecha_inicio) return toast.error('Fin no puede ser anterior a inicio');
    const netoA = parseFloat(formTarifa.precio_neto_adulto);
    if (!formTarifa.precio_neto_adulto || isNaN(netoA) || netoA <= 0) {
      return toast.error('Neto adulto inválido');
    }
    const netoN = formTarifa.precio_neto_nino ? parseFloat(formTarifa.precio_neto_nino) : null;
    if (netoN != null && (isNaN(netoN) || netoN <= 0)) return toast.error('Neto niño inválido');
    setSavingTarifa(true);
    try {
      const body = {
        paquete_id: editItem.id,
        nombre: formTarifa.nombre.trim(),
        fecha_inicio: formTarifa.fecha_inicio,
        fecha_fin: formTarifa.fecha_fin,
        precio_neto_adulto: netoA,
        precio_neto_nino: netoN,
        activa: formTarifa.activa,
        notas: formTarifa.notas?.trim() || null,
      };
      if (!editTarifa) body.empresa_id = empresaId;
      const url = editTarifa
        ? `${API_BASE}/turismo/tarifas-promocionales/${editTarifa.id}`
        : `${API_BASE}/turismo/tarifas-promocionales`;
      const res = await fetch(url, {
        method: editTarifa ? 'PUT' : 'POST',
        headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Error'); }
      toast.success(editTarifa ? 'Temporada actualizada' : 'Temporada creada');
      setTarifaDialogOpen(false);
      cargarTarifas(editItem.id);
    } catch (e) { toast.error(e.message); }
    setSavingTarifa(false);
  };

  const handleToggleActivaTarifa = async (t, nueva) => {
    setTarifas(ts => ts.map(x => x.id === t.id ? { ...x, activa: nueva } : x));
    try {
      const res = await fetch(`${API_BASE}/turismo/tarifas-promocionales/${t.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ activa: nueva }),
      });
      if (!res.ok) throw new Error('Error');
      toast.success(nueva ? 'Temporada activada' : 'Temporada desactivada');
    } catch (e) {
      setTarifas(ts => ts.map(x => x.id === t.id ? { ...x, activa: !nueva } : x));
      toast.error('No se pudo actualizar');
    }
  };

  const handleDeleteTarifa = async (t) => {
    if (!window.confirm(`¿Eliminar la temporada "${t.nombre}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/turismo/tarifas-promocionales/${t.id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('No se pudo eliminar');
      toast.success('Temporada eliminada');
      cargarTarifas(editItem.id);
    } catch (e) { toast.error(e.message); }
  };

  // ── Paquetes ──────────────────────────────────────────────────────────
  const handleNew = () => {
    setEditItem(null);
    setForm({ ...emptyForm, destino_id: destinos[0]?.id ? String(destinos[0].id) : '' });
    setTarifas([]);
    setSlideOpen(true);
  };

  const handleEdit = (row) => {
    cargarTarifas(row.id);
    cargarHistorialPrecios(row.id);
    setSimForm(emptySimForm);  // resetear simulador al abrir otro paquete
    setEditItem(row);
    setForm({
      nombre: row.nombre, destino_id: String(row.destino_id || ''),
      descripcion: row.descripcion || '',
      // Si el paquete ya tiene los campos nuevos (mig 097), usarlos. Si no,
      // fallback al precio legacy (paquetes pre-migración).
      precio_neto_adulto: String(row.precio_neto_adulto ?? row.precio_adulto ?? ''),
      precio_neto_nino: row.precio_neto_nino != null
        ? String(row.precio_neto_nino)
        : (row.precio_nino != null ? String(row.precio_nino) : ''),
      precio_pvp_adulto_directa: String(row.precio_pvp_adulto_directa ?? row.precio_adulto ?? ''),
      precio_pvp_nino_directa: row.precio_pvp_nino_directa != null
        ? String(row.precio_pvp_nino_directa)
        : (row.precio_nino != null ? String(row.precio_nino) : ''),
      precio_bebe: String(row.precio_bebe || 0),
      hora_salida_default: row.hora_salida_default || '08:00',
      hora_regreso_default: row.hora_regreso_default || '15:00',
      capacidad_maxima: String(row.capacidad_maxima || 50),
      incluye: (row.incluye || []).join('\n'),
      no_incluye: (row.no_incluye || []).join('\n'),
      drive_url: row.drive_url || '',
    });
    setSlideOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta el nombre');
    if (!form.destino_id) return toast.error('Selecciona un destino');
    const neto_a = parseFloat(form.precio_neto_adulto);
    const pvp_a = parseFloat(form.precio_pvp_adulto_directa);
    const neto_n = form.precio_neto_nino ? parseFloat(form.precio_neto_nino) : null;
    const pvp_n = form.precio_pvp_nino_directa ? parseFloat(form.precio_pvp_nino_directa) : null;
    if (!form.precio_neto_adulto || isNaN(neto_a)) return toast.error('Neto adulto inválido');
    if (!form.precio_pvp_adulto_directa || isNaN(pvp_a)) return toast.error('PVP adulto inválido');
    if (pvp_a < neto_a) return toast.error(`PVP directa adulto ($${pvp_a.toLocaleString('es-CO')}) no puede ser menor al neto ($${neto_a.toLocaleString('es-CO')})`);
    if (pvp_n != null && neto_n != null && pvp_n < neto_n) return toast.error('PVP directa niño no puede ser menor al neto niño');
    setSaving(true);
    try {
      const body = {
        nombre: form.nombre.trim(), destino_id: parseInt(form.destino_id), descripcion: form.descripcion,
        // Migración 097 — modelo neto + PVP directa
        precio_neto_adulto: neto_a,
        precio_neto_nino: neto_n,
        precio_pvp_adulto_directa: pvp_a,
        precio_pvp_nino_directa: pvp_n,
        // Legacy: pricing.py todavía usa precio_adulto/precio_nino. Rellenamos
        // con el PVP directa para que el cálculo actual siga funcionando hasta Fase B.
        precio_adulto: pvp_a,
        precio_nino: pvp_n,
        precio_bebe: parseFloat(form.precio_bebe) || 0,
        hora_salida_default: form.hora_salida_default,
        hora_regreso_default: form.hora_regreso_default,
        capacidad_maxima: parseInt(form.capacidad_maxima) || 50,
        incluye: form.incluye.split('\n').map(s => s.trim()).filter(Boolean),
        no_incluye: form.no_incluye.split('\n').map(s => s.trim()).filter(Boolean),
        drive_url: form.drive_url?.trim() || null,
      };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem ? `${API_BASE}/turismo/paquetes/${editItem.id}` : `${API_BASE}/turismo/paquetes`;
      const res = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      toast.success(editItem ? 'Paquete actualizado' : 'Paquete creado');
      setSlideOpen(false);
      cargar();
      refreshCounts?.();
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleToggleActivo = async (row, nuevoEstado) => {
    // Optimistic UI
    setPaquetes(ps => ps.map(p => p.id === row.id ? { ...p, activo: nuevoEstado } : p));
    try {
      const body = { activo: nuevoEstado };
      const res = await fetch(`${API_BASE}/turismo/paquetes/${row.id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error');
      toast.success(nuevoEstado ? 'Paquete activado' : 'Paquete desactivado');
      refreshCounts?.();
    } catch (e) {
      setPaquetes(ps => ps.map(p => p.id === row.id ? { ...p, activo: !nuevoEstado } : p));  // revert
      toast.error('No se pudo actualizar el estado');
    }
  };

  // Blindaje 29-jul-2026: pide escribir el nombre exacto del paquete antes de desactivar.
  // El backend además exige rol superadmin y hace soft-delete + auditoría.
  const [deleteTypedName, setDeleteTypedName] = useState('');
  const handleDelete = async () => {
    if (!deleteTarget) return;
    if ((deleteTypedName || '').trim().toLowerCase() !== (deleteTarget.nombre || '').trim().toLowerCase()) {
      toast.error('Debes escribir el nombre EXACTO del paquete para confirmar.');
      return;
    }
    try {
      const url = `${API_BASE}/turismo/paquetes/${deleteTarget.id}` +
                  `?confirmacion=${encodeURIComponent(deleteTarget.nombre || '')}`;
      const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail;
        const msg = (detail && typeof detail === 'object')
          ? detail.message
          : (typeof detail === 'string' ? detail : 'No se pudo desactivar');
        throw new Error(msg);
      }
      toast.success(data?.detail || 'Paquete desactivado (soft-delete)');
      setDeleteTarget(null);
      setDeleteTypedName('');
      cargar();
      refreshCounts?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Bulk delete deshabilitado como blindaje. Sólo superadmin desactiva de a uno
  // desde el botón individual, para forzar confirmación con nombre exacto por cada uno.
  const handleBulkDelete = async () => {
    toast.error('Por seguridad, los paquetes se desactivan uno a uno desde el botón individual.');
    setDeletingBulk(null);
  };

  const handleBulkToggle = async (ids, activo, clear) => {
    try {
      await Promise.all(ids.map(id =>
        fetch(`${API_BASE}/turismo/paquetes/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo }) })
      ));
      toast.success(`${ids.length} paquete(s) ${activo ? 'activado(s)' : 'desactivado(s)'}`);
      clear();
      cargar();
      refreshCounts?.();
    } catch {
      toast.error('Error en operación masiva');
    }
  };

  const handleDuplicate = async (row) => {
    try {
      // Endpoint backend clona paquete + comisiones + imágenes (NO temporadas).
      // El clon queda inactivo para que el operador lo revise antes de activar.
      const res = await fetch(`${API_BASE}/turismo/paquetes/${row.id}/duplicar`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo duplicar');
      }
      const data = await res.json();
      toast.success(
        `Paquete duplicado · ${data.comisiones_clonadas} comisiones y ${data.imagenes_clonadas} imágenes clonadas. Quedó inactivo.`
      );
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  // Columnas tabla
  const columns = [
    {
      key: 'nombre', label: 'Paquete', sortable: true,
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#0d948815', color: '#0d9488', width: 32, height: 32 }}>
            <LuggageIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }} noWrap>
              {r.nombre}
            </Typography>
            {r.descripcion && (
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>
                {r.descripcion}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: 'destino_nombre', label: 'Destino', sortable: true,
      sortValue: r => r.destino_nombre || destinos.find(d => d.id === r.destino_id)?.nombre || '',
      render: (r) => {
        const dest = r.destino_nombre || destinos.find(d => d.id === r.destino_id)?.nombre;
        return dest ? (
          <Chip size="small" icon={<BeachAccessIcon sx={{ fontSize: 13 }} />} label={dest}
            sx={{ height: 22, fontSize: 11, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }} />
        ) : <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>;
      },
    },
    { key: 'precio_adulto', label: 'Adulto', sortable: true, align: 'right',
      render: (r) => fmtMoney(r.precio_adulto) },
    { key: 'precio_nino', label: 'Niño', sortable: true, align: 'right',
      render: (r) => <span style={{ color: r.precio_nino == null ? '#cbd5e1' : '#0f172a' }}>{fmtMoney(r.precio_nino)}</span> },
    { key: 'precio_bebe', label: 'Bebé', sortable: true, align: 'right',
      render: (r) => <span style={{ color: !r.precio_bebe ? '#cbd5e1' : '#0f172a' }}>{fmtMoney(r.precio_bebe)}</span> },
    { key: 'capacidad_maxima', label: 'Cap.', sortable: true, align: 'center',
      render: (r) => <span style={{ fontSize: 11, color: '#64748b' }}>{r.capacidad_maxima || '—'}</span> },
    // Mejora #5 — Métricas operativas (últimos 30 días)
    { key: 'reservas_mes', label: 'Reservas 30d', align: 'center',
      sortValue: r => metricas[r.id]?.reservas || 0,
      render: (r) => {
        const m = metricas[r.id];
        const n = m?.reservas || 0;
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: n > 0 ? '#0d9488' : '#cbd5e1' }}>
              {n}
            </Typography>
            {n > 0 && (
              <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                {m.pax} pax
              </Typography>
            )}
          </Box>
        );
      },
    },
    { key: 'ingresos_mes', label: 'Ingresos 30d', align: 'right',
      sortValue: r => metricas[r.id]?.ingresos || 0,
      render: (r) => {
        const m = metricas[r.id];
        const v = m?.ingresos || 0;
        return (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: v > 0 ? '#0f172a' : '#cbd5e1' }}>
            {v > 0 ? fmtMoney(v) : '—'}
          </Typography>
        );
      },
    },
    { key: 'ocupacion', label: 'Ocupación 30d', align: 'center',
      sortValue: r => metricas[r.id]?.ocupacion_pct || 0,
      render: (r) => {
        const m = metricas[r.id];
        const pct = m?.ocupacion_pct || 0;
        const cap = m?.capacidad_total || 0;
        if (cap === 0) return <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>;
        const color = pct >= 70 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#64748b';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 80 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color }}>{pct}%</Typography>
            <Box sx={{ width: '100%', height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(100, pct)}%`, height: '100%', bgcolor: color, transition: 'width 0.3s' }} />
            </Box>
            <Typography sx={{ fontSize: 9.5, color: '#94a3b8' }}>{m.pax}/{cap}</Typography>
          </Box>
        );
      },
    },
    { key: 'fotos', label: 'Fotos', align: 'center',
      sortValue: r => r.imagenes_count || 0,
      render: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
          {r.imagen_principal_url ? (
            <Box sx={{ width: 36, height: 36, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <img src={`${API_BASE}${r.imagen_principal_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ) : (
            <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
              <PhotoLibraryIcon sx={{ fontSize: 18 }} />
            </Box>
          )}
          <Typography sx={{ fontSize: 11, color: r.imagenes_count > 0 ? '#0d9488' : '#94a3b8', fontWeight: 600 }}>
            {r.imagenes_count || 0}
          </Typography>
        </Box>
      ) },
    { key: 'activo', label: 'Activo', align: 'center', sortable: true,
      render: (r) => (
        <Switch size="small" checked={!!r.activo}
          onClick={e => e.stopPropagation()}
          onChange={(_, v) => handleToggleActivo(r, v)} />
      ),
    },
    { key: 'acciones', label: '', align: 'right',
      render: (r) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Duplicar">
            <IconButton size="small" onClick={() => handleDuplicate(r)} sx={{ color: '#64748b' }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#0d9488' }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={esSuperadmin()
            ? 'Desactivar (soft-delete, reversible)'
            : 'Sólo superadmin puede desactivar paquetes'}>
            <span>
              <IconButton size="small"
                disabled={!esSuperadmin()}
                onClick={() => { setDeleteTypedName(''); setDeleteTarget(r); }}
                sx={{ color: '#dc2626', '&.Mui-disabled': { color: '#cbd5e1' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const bulkActions = [
    { label: 'Activar', icon: <VisibilityIcon sx={{ fontSize: 16 }} />,
      onClick: (ids, clear) => handleBulkToggle(ids, true, clear) },
    { label: 'Desactivar', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />,
      onClick: (ids, clear) => handleBulkToggle(ids, false, clear) },
    { label: 'Eliminar', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
      onClick: (ids, clear) => setDeletingBulk({ ids, run: clear }) },
  ];

  // Si no hay destinos: forzar empty state distinto
  if (!loading && destinos.length === 0) {
    return (
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Paquetes</Typography>
        <EmptyState
          icon={<BeachAccessIcon />}
          title="Necesitas crear un destino primero"
          description="Los paquetes están vinculados a destinos. Crea al menos un destino para empezar."
          ctaLabel="Ir a Destinos"
          onCta={() => window.location.assign('/catalogo/destinos')}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Paquetes</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
          Tours, pasadías y excursiones que ofreces. Los precios y reglas se aplican al chatbot y al booking.
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={loading}
        rowKey="id"
        onRowClick={handleEdit}
        search={{ value: search, onChange: setSearch, placeholder: 'Buscar paquete por nombre o descripción...' }}
        filters={[
          { key: 'destino', label: 'Destino', value: filterDestino, onChange: setFilterDestino,
            options: destinos.map(d => ({ value: String(d.id), label: d.nombre })) },
          { key: 'estado', label: 'Estado', value: filterActivo, onChange: setFilterActivo,
            options: [
              { value: 'activos', label: 'Solo activos' },
              { value: 'inactivos', label: 'Solo inactivos' },
            ] },
        ]}
        bulkActions={bulkActions}
        actions={
          <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleNew}
            sx={{ textTransform: 'none', fontWeight: 600,
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            Nuevo Paquete
          </Button>
        }
        emptyState={
          paquetes.length === 0 ? (
            <EmptyState
              icon={<LuggageIcon />}
              title="Aún no hay paquetes"
              description="Crea tu primer paquete con destino, precios e inclusiones."
              ctaLabel="Crear paquete"
              onCta={handleNew}
            />
          ) : (
            <EmptyState
              icon={<LuggageIcon />}
              title="No hay resultados"
              description="Prueba ajustar la búsqueda o los filtros."
              size="sm"
            />
          )
        }
      />

      {/* Slide-over editor */}
      <SlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        title={editItem ? 'Editar Paquete' : 'Nuevo Paquete'}
        subtitle={editItem ? editItem.nombre : 'Configura destino, precios e inclusiones'}
        icon={<LuggageIcon />}
        variant="wide"
        footer={
          <>
            <Button onClick={() => setSlideOpen(false)} disabled={saving}
              sx={{ textTransform: 'none', color: '#64748b' }}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600,
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {saving ? 'Guardando...' : (editItem ? 'Guardar cambios' : 'Crear paquete')}
            </Button>
          </>
        }
      >
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Información básica
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <TextField label="Nombre *" size="small" fullWidth value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            placeholder="Ej: PASADIA ROSARIO BEACH" />
          <FormControl size="small" fullWidth>
            <InputLabel>Destino *</InputLabel>
            <Select label="Destino *" value={form.destino_id}
              onChange={e => setForm({ ...form, destino_id: e.target.value })}>
              {destinos.map(d => <MenuItem key={d.id} value={String(d.id)}>{d.nombre}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Descripción" size="small" fullWidth multiline rows={2}
            value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} />
        </Box>

        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Precios
        </Typography>
        {/* Tarifa neta (lo que recibe Entretur, sin comisión) */}
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#166534', mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            💰 Tarifa neta (lo que recibe Entretur)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="Neto adulto *" size="small" type="number" value={form.precio_neto_adulto}
              onChange={e => setForm({ ...form, precio_neto_adulto: e.target.value })} />
            <TextField label="Neto niño" size="small" type="number" value={form.precio_neto_nino}
              onChange={e => setForm({ ...form, precio_neto_nino: e.target.value })}
              placeholder="Vacío si no acepta niños" />
          </Box>
        </Box>
        {/* PVP venta directa Entretur. Comisión directa = PVP - neto. */}
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e3a8a', mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            🏷️ PVP venta directa Entretur (a cliente final)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="PVP adulto *" size="small" type="number" value={form.precio_pvp_adulto_directa}
              onChange={e => setForm({ ...form, precio_pvp_adulto_directa: e.target.value })} />
            <TextField label="PVP niño" size="small" type="number" value={form.precio_pvp_nino_directa}
              onChange={e => setForm({ ...form, precio_pvp_nino_directa: e.target.value })}
              placeholder="Vacío si no acepta niños" />
          </Box>
          {(() => {
            const pvpA = parseFloat(form.precio_pvp_adulto_directa) || 0;
            const netoA = parseFloat(form.precio_neto_adulto) || 0;
            const pvpN = parseFloat(form.precio_pvp_nino_directa) || 0;
            const netoN = parseFloat(form.precio_neto_nino) || 0;
            const comA = pvpA - netoA;
            const comN = pvpN - netoN;
            if (pvpA <= 0 && pvpN <= 0) return null;
            return (
              <Typography sx={{ fontSize: 11, color: '#1e40af', mt: 1 }}>
                Comisión Entretur directa:&nbsp;
                {pvpA > 0 && <>adulto <b>${comA.toLocaleString('es-CO')}</b></>}
                {pvpA > 0 && pvpN > 0 && ' · '}
                {pvpN > 0 && <>niño <b>${comN.toLocaleString('es-CO')}</b></>}
              </Typography>
            );
          })()}
        </Box>

        {/* Tarifas estacionales — Migración 099. Override del neto por rango de
            fechas. Cuando la salida cae en una temporada activa, pricing.py usa
            el neto de la temporada. Fuera del rango, vuelve al neto normal. */}
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#faf5ff', border: '1px solid #d8b4fe' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocalOfferIcon sx={{ fontSize: 18, color: '#7c3aed', mr: 0.75 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#581c87', textTransform: 'uppercase', letterSpacing: 0.3, flex: 1 }}>
              🗓️ Tarifas estacionales (programadas por calendario)
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleNewTarifa}
              disabled={!editItem}
              sx={{ textTransform: 'none', fontSize: 12, color: '#7c3aed' }}>
              Agregar temporada
            </Button>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#581c87', mb: 1 }}>
            Override del neto para un rango de fechas. El PVP no cambia. Fuera del rango se cobra el neto normal automáticamente.
          </Typography>
          {!editItem ? (
            <Alert severity="info" sx={{ fontSize: 11.5, py: 0.5 }}>
              Guarda el paquete primero para poder agregar temporadas.
            </Alert>
          ) : tarifas.length === 0 ? (
            <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fff', borderRadius: 1.5, border: '1px dashed #d8b4fe' }}>
              <Typography sx={{ fontSize: 11.5, color: '#7c3aed' }}>
                Sin temporadas programadas. Se cobra el neto normal todos los días.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e9d5ff', overflow: 'hidden' }}>
              {tarifas.map((t, idx) => {
                const cat = categorizarTarifa(t);
                const chipCfg = {
                  vigente:   { label: '🟢 Vigente',  bg: '#dcfce7', col: '#166534' },
                  futura:    { label: '⏳ Futura',    bg: '#dbeafe', col: '#1e40af' },
                  vencida:   { label: '⌛ Vencida',  bg: '#fef3c7', col: '#854d0e' },
                  inactiva:  { label: '⏸️ Inactiva', bg: '#f1f5f9', col: '#64748b' },
                }[cat];
                const fmtFecha = (s) => {
                  if (!s) return '—';
                  const d = new Date(s + 'T00:00:00');
                  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: '2-digit' });
                };
                return (
                  <Box key={t.id} sx={{
                    p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
                    borderBottom: idx < tarifas.length - 1 ? '1px solid #f3e8ff' : 'none',
                    '&:hover': { bgcolor: '#faf5ff' },
                  }}>
                    <Chip label={chipCfg.label} size="small"
                      sx={{ bgcolor: chipCfg.bg, color: chipCfg.col, fontWeight: 700, fontSize: 10.5, height: 20 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{t.nombre}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EventIcon sx={{ fontSize: 12 }} />
                        {fmtFecha(t.fecha_inicio)} → {fmtFecha(t.fecha_fin)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                        Adulto ${Number(t.precio_neto_adulto).toLocaleString('es-CO')}
                      </Typography>
                      {t.precio_neto_nino != null && (
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          Niño ${Number(t.precio_neto_nino).toLocaleString('es-CO')}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={t.activa ? 'Desactivar' : 'Activar'}>
                      <Switch size="small" checked={t.activa}
                        onChange={(e) => handleToggleActivaTarifa(t, e.target.checked)} />
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleEditTarifa(t)} sx={{ color: '#0d9488' }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteTarifa(t)} sx={{ color: '#dc2626' }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Mejora #3 — Simulador de precio en vivo. Sirve para validar que el
            setup (neto, PVP, comisión por agencia, temporadas) produce los
            precios esperados sin tener que crear una reserva real. */}
        <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#854d0e', mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            🧮 Simulador de precio (en vivo)
          </Typography>
          {!editItem ? (
            <Alert severity="info" sx={{ fontSize: 11.5, py: 0.5 }}>
              Guarda el paquete primero para simular precios.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 1.5 }}>
                <TextField label="Fecha" size="small" type="date" value={simForm.fecha}
                  onChange={e => setSimForm({ ...simForm, fecha: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
                <TextField label="Adultos" size="small" type="number" value={simForm.adultos}
                  onChange={e => setSimForm({ ...simForm, adultos: e.target.value })}
                  inputProps={{ min: 0 }} />
                <TextField label="Niños" size="small" type="number" value={simForm.ninos}
                  onChange={e => setSimForm({ ...simForm, ninos: e.target.value })}
                  inputProps={{ min: 0 }} />
                <TextField label="Bebés" size="small" type="number" value={simForm.bebes}
                  onChange={e => setSimForm({ ...simForm, bebes: e.target.value })}
                  inputProps={{ min: 0 }} />
                <FormControl size="small">
                  <InputLabel>Vendedor</InputLabel>
                  <Select label="Vendedor" value={simForm.agencia_id}
                    onChange={e => setSimForm({ ...simForm, agencia_id: e.target.value })}>
                    <MenuItem value="">Venta directa Entretur</MenuItem>
                    {agencias.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              {simulando && (
                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Calculando…</Typography>
              )}
              {simResult && !simulando && (() => {
                const r = simResult;
                const isAgencia = !!simForm.agencia_id;
                const ag = agencias.find(a => String(a.id) === String(simForm.agencia_id));
                return (
                  <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, p: 1.5, border: '1px solid #fde68a' }}>
                    {r.promo_aplicada && (
                      <Box sx={{ mb: 1, p: 0.75, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px dashed #86efac' }}>
                        <Typography sx={{ fontSize: 11, color: '#166534' }}>
                          🏷️ Promo aplicada: <b>{r.promo_aplicada.nombre}</b>
                          {' '}({r.promo_aplicada.fecha_inicio} → {r.promo_aplicada.fecha_fin})
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.5, fontSize: 12 }}>
                      <Typography sx={{ fontSize: 12, color: '#475569' }}>
                        Adultos · ${Math.round(r.precio_adulto_unitario).toLocaleString('es-CO')} × {r.desglose?.adultos?.cantidad || 0}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>
                        ${Math.round(r.desglose?.adultos?.subtotal || 0).toLocaleString('es-CO')}
                      </Typography>
                      {(r.desglose?.ninos?.cantidad || 0) > 0 && (
                        <>
                          <Typography sx={{ fontSize: 12, color: '#475569' }}>
                            Niños · ${Math.round(r.precio_nino_unitario).toLocaleString('es-CO')} × {r.desglose?.ninos?.cantidad}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>
                            ${Math.round(r.desglose?.ninos?.subtotal || 0).toLocaleString('es-CO')}
                          </Typography>
                        </>
                      )}
                      <Divider sx={{ gridColumn: '1 / -1', my: 0.25 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                        Total al cliente
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0d9488', textAlign: 'right' }}>
                        ${Math.round(r.total).toLocaleString('es-CO')}
                      </Typography>
                      <Divider sx={{ gridColumn: '1 / -1', my: 0.25 }} />
                      {isAgencia ? (
                        <>
                          <Typography sx={{ fontSize: 11.5, color: '#a16207' }}>
                            🟡 Comisión {ag?.nombre || 'agencia'} (efectiva)
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a16207', textAlign: 'right' }}>
                            ${Math.round(r.comision_valor || 0).toLocaleString('es-CO')}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: '#0c4a6e' }}>
                            💰 Neto que recibe Entretur
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', textAlign: 'right' }}>
                            ${Math.round(r.neto_total_entretur || 0).toLocaleString('es-CO')}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: 11.5, color: '#a16207' }}>
                            🟡 Margen Entretur (PVP − neto)
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a16207', textAlign: 'right' }}>
                            ${Math.round(r.comision_entretur_directa || 0).toLocaleString('es-CO')}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: '#0c4a6e' }}>
                            💰 Neto que recibe Entretur
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', textAlign: 'right' }}>
                            ${Math.round(r.neto_total_entretur || 0).toLocaleString('es-CO')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })()}
              {!simResult && !simulando && (
                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                  Cambia los inputs para ver el cálculo en vivo.
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Mejora #4 — Histórico de cambios de precio. Cada vez que se edita
            un campo de precio del paquete se registra una entrada con quién y
            cuándo. Sirve para responder "este precio no es el que pactamos". */}
        {editItem && (
          <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#334155', mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              📜 Histórico de cambios de precio
            </Typography>
            {historialPrecios.length === 0 ? (
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                Sin cambios registrados. Cada edición que toque un campo de precio queda registrada aquí.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {historialPrecios.map((h, idx) => {
                  const fecha = new Date(h.created_at);
                  const fechaStr = fecha.toLocaleString('es-CO', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  });
                  const cambios = Object.values(h.cambios || {});
                  return (
                    <Box key={h.id} sx={{
                      p: 1, borderRadius: 1.5, bgcolor: '#fff',
                      mb: idx < historialPrecios.length - 1 ? 0.75 : 0,
                      border: '1px solid #e2e8f0',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>
                          {h.usuario_nombre || h.usuario_email || 'Usuario desconocido'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{fechaStr}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {cambios.map((c, i) => {
                          const baja = (c.a ?? 0) < (c.de ?? 0);
                          const color = baja ? '#dc2626' : '#16a34a';
                          const bg = baja ? '#fee2e2' : '#dcfce7';
                          return (
                            <Chip key={i} size="small" sx={{ bgcolor: bg, color, fontSize: 10.5, height: 22, fontWeight: 600 }}
                              label={
                                <>
                                  {c.label}: ${Number(c.de || 0).toLocaleString('es-CO')}
                                  {' '}{baja ? '↓' : '↑'}{' '}
                                  ${Number(c.a || 0).toLocaleString('es-CO')}
                                </>
                              } />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Bebés: configurable pero generalmente $0 */}
        <Box sx={{ mb: 3 }}>
          <TextField label="Precio bebé (normalmente $0)" size="small" type="number" value={form.precio_bebe}
            onChange={e => setForm({ ...form, precio_bebe: e.target.value })}
            sx={{ width: 280 }}
            helperText="Bebés normalmente no pagan ni generan comisión" />
        </Box>

        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Horario y capacidad
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
          <TextField label="Hora salida" size="small" type="time" value={form.hora_salida_default}
            onChange={e => setForm({ ...form, hora_salida_default: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Hora regreso" size="small" type="time" value={form.hora_regreso_default}
            onChange={e => setForm({ ...form, hora_regreso_default: e.target.value })}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Capacidad máx" size="small" type="number" value={form.capacidad_maxima}
            onChange={e => setForm({ ...form, capacidad_maxima: e.target.value })} />
        </Box>

        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Inclusiones (uno por línea)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 3 }}>
          <TextField label="Incluye" size="small" multiline rows={4} value={form.incluye}
            onChange={e => setForm({ ...form, incluye: e.target.value })}
            placeholder="Almuerzo&#10;Snorkel&#10;Bebidas" />
          <TextField label="No incluye" size="small" multiline rows={4} value={form.no_incluye}
            onChange={e => setForm({ ...form, no_incluye: e.target.value })}
            placeholder="Impuesto portuario&#10;Bebidas alcohólicas" />
        </Box>

        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          <PhotoLibraryIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
          Galería de fotos
        </Typography>
        <GaleriaImagenes
          entidadId={editItem?.id}
          endpoint="/turismo/paquetes"
          color="#0d9488"
        />

        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1 }}>
          Link externo (opcional)
        </Typography>
        <TextField label="URL de carpeta Google Drive" size="small" fullWidth value={form.drive_url}
          onChange={e => setForm({ ...form, drive_url: e.target.value })}
          placeholder="https://drive.google.com/..."
          InputProps={{ startAdornment: <CollectionsIcon sx={{ fontSize: 18, color: '#94a3b8', mr: 1 }} /> }} />
      </SlideOver>

      {/* Blindaje: para desactivar hay que escribir el nombre EXACTO del paquete.
          Además el backend exige rol superadmin y hace soft-delete + auditoría. */}
      <Dialog open={!!deleteTarget} onClose={() => { setDeleteTarget(null); setDeleteTypedName(''); }}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                          color: '#fff', fontSize: 16, fontWeight: 800 }}>
          Desactivar paquete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
            El paquete <strong>"{deleteTarget?.nombre}"</strong> se marcará como inactivo.
            Es reversible desde el switch "Activo" de la fila, pero desaparecerá del catálogo
            visible al chatbot y a los vendedores.
          </Alert>
          <Alert severity="info" icon={false} sx={{
            mb: 2, fontSize: 12, bgcolor: 'rgba(30, 58, 95, 0.06)',
            color: '#1e3a5f', border: '1px solid rgba(30, 58, 95, 0.15)',
          }}>
            🔒 Esta acción quedará registrada permanentemente con tu email,
            tu IP y la hora exacta en el log de auditoría.
          </Alert>
          <Typography sx={{ fontSize: 13, color: '#334155', mb: 1 }}>
            Para confirmar, escribe el nombre exacto del paquete:
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1, fontFamily: 'monospace' }}>
            {deleteTarget?.nombre}
          </Typography>
          <TextField
            autoFocus fullWidth size="small"
            value={deleteTypedName}
            onChange={e => setDeleteTypedName(e.target.value)}
            placeholder="Escribe el nombre exacto"
            onKeyDown={e => { if (e.key === 'Enter') handleDelete(); }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setDeleteTarget(null); setDeleteTypedName(''); }}
            sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" color="error"
            disabled={(deleteTypedName || '').trim().toLowerCase() !==
                      (deleteTarget?.nombre || '').trim().toLowerCase()}
            onClick={handleDelete}>
            Desactivar paquete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete: bloqueado por seguridad. El diálogo se abre pero no permite avanzar. */}
      <ConfirmDialog
        open={!!deletingBulk}
        onClose={() => setDeletingBulk(null)}
        onConfirm={handleBulkDelete}
        title="Desactivación masiva no permitida"
        message="Por seguridad, los paquetes se desactivan uno a uno desde el botón individual de cada fila (solo superadmin). Esto obliga a confirmar cada uno escribiendo su nombre exacto."
        confirmText="Entendido"
      />

      {/* Sub-dialog: crear/editar temporada estacional */}
      <Dialog open={tarifaDialogOpen} onClose={() => setTarifaDialogOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <LocalOfferIcon />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
              {editTarifa ? 'Editar temporada' : 'Nueva temporada estacional'}
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
              {editItem?.nombre} · Override del neto para un rango de fechas
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField label="Nombre de la temporada *" size="small" fullWidth value={formTarifa.nombre}
              onChange={e => setFormTarifa({ ...formTarifa, nombre: e.target.value })}
              placeholder="Ej: Temporada baja junio-agosto" />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField label="Fecha inicio *" size="small" type="date" value={formTarifa.fecha_inicio}
                onChange={e => setFormTarifa({ ...formTarifa, fecha_inicio: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Fecha fin *" size="small" type="date" value={formTarifa.fecha_fin}
                onChange={e => setFormTarifa({ ...formTarifa, fecha_fin: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            {editItem && (
              <Alert severity="info" sx={{ fontSize: 11.5, py: 0.5 }}>
                Neto normal: adulto <b>${Number(editItem.precio_neto_adulto || 0).toLocaleString('es-CO')}</b>
                {editItem.precio_neto_nino != null && <> · niño <b>${Number(editItem.precio_neto_nino).toLocaleString('es-CO')}</b></>}
                . El PVP se mantiene en <b>${Number(editItem.precio_pvp_adulto_directa || 0).toLocaleString('es-CO')}</b>.
              </Alert>
            )}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#166534', mb: 1, textTransform: 'uppercase' }}>
                💰 Neto durante la temporada
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField label="Neto adulto *" size="small" type="number" value={formTarifa.precio_neto_adulto}
                  onChange={e => setFormTarifa({ ...formTarifa, precio_neto_adulto: e.target.value })} />
                <TextField label="Neto niño" size="small" type="number" value={formTarifa.precio_neto_nino}
                  onChange={e => setFormTarifa({ ...formTarifa, precio_neto_nino: e.target.value })}
                  placeholder="Vacío = usa neto normal niño" />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={formTarifa.activa} onChange={e => setFormTarifa({ ...formTarifa, activa: e.target.checked })} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {formTarifa.activa ? 'Activa (las activas no pueden traslapar fechas con otra temporada)' : 'Inactiva'}
              </Typography>
            </Box>
            <TextField label="Notas (opcional)" size="small" fullWidth multiline rows={2}
              value={formTarifa.notas} onChange={e => setFormTarifa({ ...formTarifa, notas: e.target.value })}
              placeholder="Contexto interno: motivo, autorización, etc." />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTarifaDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button onClick={handleSaveTarifa} variant="contained" disabled={savingTarifa}
            sx={{ textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' }}>
            {savingTarifa ? 'Guardando...' : (editTarifa ? 'Guardar cambios' : 'Crear temporada')}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </Box>
  );
}
